/**
 * Validação por etapa do formulário de onboarding.
 * Retorna Record<string, string> onde key é o caminho do campo e value a mensagem de erro.
 */
import {
  validateCPF,
  validateCNPJ,
  validatePhone,
  validateCEP,
  validateCNAE,
  validateDate,
  validateAge,
  validateEmail,
  validateRequired,
  validatePositiveNumber,
  validateSharePercentages,
} from './validators';
import {
  OnboardingFormData,
  CustomerType,
  FormStep,
  PersonIdentification,
  Address,
  CompanyIdentification,
  FinancialInfo,
  Shareholder,
  FinalBeneficiary,
  AuthorizationsForm,
} from '../types';
import { UploadedFileInfo } from '../services/api';

export type FieldErrors = Record<string, string>;

/**
 * Extrai erros cujas chaves começam com `prefix.` e retorna sem o prefixo.
 * Ex: pickErrors({ 'residentialAddress.zipCode': 'x' }, 'residentialAddress') → { zipCode: 'x' }
 */
export function pickErrors(errors: FieldErrors, prefix: string): Record<string, string> {
  const result: Record<string, string> = {};
  const p = `${prefix}.`;
  for (const key in errors) {
    if (key.startsWith(p)) {
      result[key.slice(p.length)] = errors[key];
    }
  }
  return result;
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function req(value: unknown, key: string, msg: string, errors: FieldErrors) {
  if (!validateRequired(value)) errors[key] = msg;
}

/** Campo numérico obrigatório e maior que zero. Retorna true se houve erro. */
function reqPositive(value: number | undefined, key: string, t: (k: string) => string, errors: FieldErrors): boolean {
  if (value === undefined || value === null) {
    errors[key] = t('validation.required');
    return true;
  }
  if (!validatePositiveNumber(value)) {
    errors[key] = t('validation.positiveNumber');
    return true;
  }
  return false;
}

// --------------------------------------------------------------------------
// Address validation (reusável para residencial, comercial, matriz)
// --------------------------------------------------------------------------
function validateAddress(
  addr: Partial<Address> | undefined,
  prefix: string,
  t: (key: string) => string,
  opts: { requirePhones?: boolean } = {},
): FieldErrors {
  const e: FieldErrors = {};
  if (!addr) {
    e[`${prefix}.zipCode`] = t('validation.required');
    return e;
  }

  req(addr.zipCode, `${prefix}.zipCode`, t('validation.required'), e);
  if (addr.zipCode && !validateCEP(addr.zipCode)) e[`${prefix}.zipCode`] = t('validation.invalidCEP');

  req(addr.street, `${prefix}.street`, t('validation.required'), e);
  req(addr.number, `${prefix}.number`, t('validation.required'), e);
  req(addr.neighborhood, `${prefix}.neighborhood`, t('validation.required'), e);
  req(addr.city, `${prefix}.city`, t('validation.required'), e);
  req(addr.state, `${prefix}.state`, t('validation.required'), e);

  if (opts.requirePhones) {
    req(addr.mobilePhone, `${prefix}.mobilePhone`, t('validation.required'), e);
    if (addr.mobilePhone && !validatePhone(addr.mobilePhone)) {
      e[`${prefix}.mobilePhone`] = t('validation.invalidPhone');
    }
  }

  if (addr.homePhone && !validatePhone(addr.homePhone)) {
    e[`${prefix}.homePhone`] = t('validation.invalidPhone');
  }
  if (!opts.requirePhones && addr.mobilePhone && !validatePhone(addr.mobilePhone)) {
    e[`${prefix}.mobilePhone`] = t('validation.invalidPhone');
  }

  return e;
}

// --------------------------------------------------------------------------
// PF — Identification step
// --------------------------------------------------------------------------
function validatePFIdentification(
  data: Partial<OnboardingFormData>,
  t: (key: string) => string,
): FieldErrors {
  const e: FieldErrors = {};
  const pf = data.pessoaFisica;
  const id: Partial<PersonIdentification> = pf?.identification || {};

  // --- Identificação ---
  req(id.fullName, 'identification.fullName', t('validation.required'), e);
  req(id.cpf, 'identification.cpf', t('validation.required'), e);
  if (id.cpf && !validateCPF(id.cpf)) e['identification.cpf'] = t('validation.invalidCPF');

  req(id.maritalStatus, 'identification.maritalStatus', t('validation.required'), e);
  req(id.nationality, 'identification.nationality', t('validation.required'), e);

  req(id.birthDate, 'identification.birthDate', t('validation.required'), e);
  if (id.birthDate) {
    if (!validateDate(id.birthDate)) e['identification.birthDate'] = t('validation.invalidDate');
    else if (!validateAge(id.birthDate)) e['identification.birthDate'] = t('validation.underAge');
  }

  req(id.documentType, 'identification.documentType', t('validation.required'), e);
  req(id.documentNumber, 'identification.documentNumber', t('validation.required'), e);
  req(id.documentUf, 'identification.documentUf', t('validation.required'), e);
  req(id.gender, 'identification.gender', t('validation.required'), e);
  req(id.motherName, 'identification.motherName', t('validation.required'), e);

  // Cônjuge (condicional)
  const needsSpouse = id.maritalStatus === 'casado' || id.maritalStatus === 'uniao_estavel';
  if (needsSpouse) {
    req(id.spouseName, 'identification.spouseName', t('validation.required'), e);
    req(id.spouseCpf, 'identification.spouseCpf', t('validation.required'), e);
    if (id.spouseCpf && !validateCPF(id.spouseCpf)) {
      e['identification.spouseCpf'] = t('validation.invalidCPF');
    }
  }

  // --- Endereço residencial ---
  Object.assign(e, validateAddress(pf?.residentialAddress, 'residentialAddress', t, { requirePhones: true }));

  // --- Endereço comercial ---
  if (!pf?.sameAsResidential) {
    Object.assign(e, validateAddress(pf?.commercialAddress, 'commercialAddress', t));
  }

  // --- Atividade profissional ---
  req(pf?.professional?.mainActivity, 'professional.mainActivity', t('validation.required'), e);

  return e;
}

// --------------------------------------------------------------------------
// PJ — Identification step
// --------------------------------------------------------------------------
function validatePJIdentification(
  data: Partial<OnboardingFormData>,
  t: (key: string) => string,
): FieldErrors {
  const e: FieldErrors = {};
  const pj = data.pessoaJuridica;
  const id: Partial<CompanyIdentification> = pj?.identification || {};

  // --- Identificação ---
  req(id.companyName, 'identification.companyName', t('validation.required'), e);

  req(id.cnpj, 'identification.cnpj', t('validation.required'), e);
  if (id.cnpj && !validateCNPJ(id.cnpj)) e['identification.cnpj'] = t('validation.invalidCNPJ');

  req(id.constitutionDate, 'identification.constitutionDate', t('validation.required'), e);
  if (id.constitutionDate && !validateDate(id.constitutionDate)) {
    e['identification.constitutionDate'] = t('validation.invalidDate');
  }

  req(id.mainActivity, 'identification.mainActivity', t('validation.required'), e);

  req(id.cnaeCode, 'identification.cnaeCode', t('validation.required'), e);
  if (id.cnaeCode && !validateCNAE(id.cnaeCode)) e['identification.cnaeCode'] = t('validation.invalidCNAE');

  // --- Endereço matriz ---
  Object.assign(e, validateAddress(pj?.headquartersAddress, 'headquartersAddress', t));

  // --- Contato ---
  req(pj?.contactPhone, 'contactPhone', t('validation.required'), e);
  if (pj?.contactPhone && !validatePhone(pj.contactPhone)) e['contactPhone'] = t('validation.invalidPhone');
  req(pj?.contactPerson, 'contactPerson', t('validation.required'), e);

  // --- Financeiro ---
  const fin: Partial<FinancialInfo> = pj?.financial || {};
  reqPositive(fin.capital, 'financial.capital', t, e);
  req(fin.country, 'financial.country', t('validation.required'), e);
  reqPositive(fin.annualRevenue, 'financial.annualRevenue', t, e);
  reqPositive(fin.netWorth, 'financial.netWorth', t, e);

  // --- Sócios ---
  const shareholders: Shareholder[] = pj?.shareholders || [];
  if (shareholders.length === 0) {
    e['shareholders'] = t('validation.required');
  } else {
    shareholders.forEach((s, i) => {
      req(s.name, `shareholders[${i}].name`, t('validation.required'), e);
      req(s.cpfCnpj, `shareholders[${i}].cpfCnpj`, t('validation.required'), e);
      if (s.cpfCnpj) {
        const cleaned = s.cpfCnpj.replace(/\D/g, '');
        if (cleaned.length <= 11 && !validateCPF(s.cpfCnpj)) {
          e[`shareholders[${i}].cpfCnpj`] = t('validation.invalidCPF');
        } else if (cleaned.length > 11 && !validateCNPJ(s.cpfCnpj)) {
          e[`shareholders[${i}].cpfCnpj`] = t('validation.invalidCNPJ');
        }
      }
    });
    const pcts = shareholders.map((s) => s.sharePercentage || 0);
    if (!validateSharePercentages(pcts)) {
      e['shareholders.percentage'] = t('validation.invalidSharePercentage');
    }
  }

  // --- Beneficiários finais (opcional, mas se existir deve estar completo) ---
  const beneficiaries: FinalBeneficiary[] = pj?.finalBeneficiaries || [];
  beneficiaries.forEach((b, i) => {
    req(b.name, `finalBeneficiaries[${i}].name`, t('validation.required'), e);
    req(b.cpf, `finalBeneficiaries[${i}].cpf`, t('validation.required'), e);
    if (b.cpf && !validateCPF(b.cpf)) {
      e[`finalBeneficiaries[${i}].cpf`] = t('validation.invalidCPF');
    }
  });

  return e;
}

// --------------------------------------------------------------------------
// Credit Questionnaire — validação mínima (muitos campos são opcionais/N/A)
// --------------------------------------------------------------------------
function validateCreditQuestionnaire(
  data: Partial<OnboardingFormData>,
  t: (key: string) => string,
): FieldErrors {
  const e: FieldErrors = {};
  const cq = data.creditQuestionnaire;
  if (!cq) return e;

  const cd = cq.clientData;
  if (cd) {
    req(cd.producerName, 'clientData.producerName', t('validation.required'), e);
    req(cd.cpf, 'clientData.cpf', t('validation.required'), e);
    if (cd.cpf && !validateCPF(cd.cpf)) e['clientData.cpf'] = t('validation.invalidCPF');
    req(cd.phone, 'clientData.phone', t('validation.required'), e);
    if (cd.phone && !validatePhone(cd.phone)) e['clientData.phone'] = t('validation.invalidPhone');
    req(cd.financedSeason, 'clientData.financedSeason', t('validation.required'), e);

    // Contato condicional
    if (cd.contactPreference?.canContact) {
      req(cd.contactPreference.contactName, 'clientData.contactPreference.contactName', t('validation.required'), e);
      req(cd.contactPreference.contactEmail, 'clientData.contactPreference.contactEmail', t('validation.required'), e);
      if (cd.contactPreference.contactEmail && !validateEmail(cd.contactPreference.contactEmail)) {
        e['clientData.contactPreference.contactEmail'] = t('validation.invalidEmail');
      }
      req(cd.contactPreference.contactPhone, 'clientData.contactPreference.contactPhone', t('validation.required'), e);
      if (cd.contactPreference.contactPhone && !validatePhone(cd.contactPreference.contactPhone)) {
        e['clientData.contactPreference.contactPhone'] = t('validation.invalidPhone');
      }
    }

    // Membros do grupo (condicional)
    if (cd.plantsInGroup && cd.groupMembers) {
      cd.groupMembers.forEach((m, i) => {
        req(m.name, `clientData.groupMembers[${i}].name`, t('validation.required'), e);
        req(m.cpf, `clientData.groupMembers[${i}].cpf`, t('validation.required'), e);
        if (m.cpf && !validateCPF(m.cpf)) {
          e[`clientData.groupMembers[${i}].cpf`] = t('validation.invalidCPF');
        }
      });
    }
  } else {
    e['clientData'] = t('validation.required');
  }

  return e;
}

// --------------------------------------------------------------------------
// Authorizations
// --------------------------------------------------------------------------
function validateAuthorizations(
  data: Partial<OnboardingFormData>,
  t: (key: string) => string,
): FieldErrors {
  const e: FieldErrors = {};
  const auth: Partial<AuthorizationsForm> = data.authorizations || {};

  if (!auth.allAccepted) {
    e['authorizations.allAccepted'] = t('validation.allAuthorizationsRequired');
  }

  return e;
}

// --------------------------------------------------------------------------
// Documents
// --------------------------------------------------------------------------
function validateDocuments(
  _data: Partial<OnboardingFormData>,
  uploadedFiles: Record<string, UploadedFileInfo>,
  requiredDocIds: string[],
  t: (key: string) => string,
): FieldErrors {
  const e: FieldErrors = {};

  for (const docId of requiredDocIds) {
    if (!uploadedFiles[docId]) {
      e[`documents.${docId}`] = t('validation.documentRequired');
    }
  }

  return e;
}

// --------------------------------------------------------------------------
// Public API — validação por step
// --------------------------------------------------------------------------

export function validateStep(
  step: FormStep,
  data: Partial<OnboardingFormData>,
  customerType: CustomerType,
  t: (key: string) => string,
  opts?: {
    uploadedFiles?: Record<string, UploadedFileInfo>;
    requiredDocIds?: string[];
  },
): FieldErrors {
  switch (step) {
    case 'identification':
      return customerType === 'PF'
        ? validatePFIdentification(data, t)
        : validatePJIdentification(data, t);

    case 'credit-questionnaire':
      return validateCreditQuestionnaire(data, t);

    case 'authorizations':
      return validateAuthorizations(data, t);

    case 'documents':
      return validateDocuments(data, opts?.uploadedFiles || {}, opts?.requiredDocIds || [], t);

    // Steps sem validação obrigatória (review é o último)
    case 'review':
    default:
      return {};
  }
}
